import {
	createQueryInputSchema,
	QUERY_DESCRIPTION_MAX_LENGTH,
	type QueryDetail,
	richTextVisibleLength,
} from "@anthra/platform/domain"
import { toDisplayable } from "@anthra/platform/errors"
import {
	type FileRejection,
	formatBytes,
	type PlatformFile,
	QUERY_ATTACHMENT_RULES,
} from "@anthra/platform/files"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { controlClasses } from "@anthra/ui/control"
import { Field } from "@anthra/ui/field"
import { TiptapEditor } from "@anthra/ui/tiptap-editor"
import { PageTitle } from "@anthra/ui/typography"
import { createFileRoute, Link } from "@tanstack/react-router"
import { CheckIcon, ClockIcon, FileTextIcon, TriangleAlertIcon, UploadIcon } from "lucide-react"
import { useRef, useState } from "react"

import { useCreateQuery } from "@/features/queries/use-queries"
import { toPlatformFile } from "@/lib/files"
import { useQueryDraftStore } from "@/stores/use-query-draft-store"

/**
 * Ask an expert about a notice (US-00-002.1/.2/.4/.5/.27).
 *
 * Built against screens/create-query.html: the response-time banner, the card
 * holding the title/category/description fields and the attachment picker.
 *
 * THE FORM'S TEXT LIVES IN A STORE, NOT IN THIS COMPONENT. A session that lapses
 * mid-submit sends the person to OTP sign-in and brings them back here with what
 * they typed intact (US-00-002.26), and the retry after a connectivity failure
 * reuses the FIRST attempt's idempotency key so it creates exactly one query
 * (US-00-002.27).
 *
 * DRAFT CLEARING: the store is reset on every entry to this route UNLESS a
 * submission is in flight (`idempotencyKey` is set) — that is the OTP-lapse
 * signal, and it is the one case where last session's text is wanted. Cancel
 * resets explicitly too, so a failed submission's draft does not bleed through
 * when the person deliberately abandons the form.
 */
export const Route = createFileRoute("/_app/queries/new")({
	beforeLoad: () => {
		const draft = useQueryDraftStore.getState()
		if (draft.idempotencyKey === null) draft.reset()
	},
	component: CreateQuery,
})

function CreateQuery() {
	const draft = useQueryDraftStore()
	const create = useCreateQuery()
	const [errors, setErrors] = useState<Record<string, string>>({})
	// Held here rather than navigated away from: screens/create-query.html's
	// state 5 is a receipt on THIS route, with "Open query", "Go to My queries"
	// and "Ask another question" as three separate choices, not one redirect.
	const [submitted, setSubmitted] = useState<QueryDetail | null>(null)

	function submit(): void {
		const parsed = createQueryInputSchema.safeParse({
			title: draft.title,
			description: draft.description,
		})

		if (!parsed.success) {
			const fieldErrors: Record<string, string> = {}
			for (const issue of parsed.error.issues) {
				const field = issue.path[0]
				if (typeof field === "string") fieldErrors[field] ??= issue.message
			}
			setErrors(fieldErrors)
			return
		}

		setErrors({})
		create.mutate(
			{
				input: parsed.data,
				attachments: draft.attachments,
				idempotencyKey: draft.submissionKey(),
			},
			{
				onSuccess: detail => {
					draft.reset()
					setSubmitted(detail)
				},
			},
		)
	}

	if (submitted) {
		return (
			<SubmittedQuery
				detail={submitted}
				onAskAnother={() => {
					setSubmitted(null)
				}}
			/>
		)
	}

	return (
		<div
			className="flex flex-col gap-6"
			data-screen="create-query"
		>
			<PageTitle>Ask a query</PageTitle>

			{/* <ResponseTimeBanner /> */}

			{/* A <summary> is `display: list-item` by default, so its marker,
			    the icon and the label stacked as three blocks. `list-none` drops
			    the marker and the flex row puts the icon beside the words. */}
			{/* THE FORM AND ITS RAIL, side by side.

			    The root was `flex flex-col gap-6` with the aside as its last
			    sibling, so the rail stacked under a form that ran the full
			    width of the page — a one-line Title input 930px wide at 1440.
			    _app.vetting.new, the other create screen, has always been a
			    two-column grid; this is the same shape. */}
			<div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
				<details className="border-border rounded-card border p-3 lg:hidden">
					<summary className="text-field-label text-foreground flex cursor-pointer list-none items-center gap-2">
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
						>
							<circle
								cx="12"
								cy="12"
								r="8.5"
							/>
							<path d="M12 16v-4.5M12 8v.01" />
						</svg>
						What to include
					</summary>
					<ul className="text-description text-subtle-foreground flex list-disc flex-col gap-1.5 pl-5">
						<li>The assessment year the notice is for</li>
						<li>Any amount, date or deadline printed on it</li>
						<li>What you have already tried or replied</li>
					</ul>
				</details>

				<div className="flex flex-col gap-4">
					<form
						className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4"
						onSubmit={event => {
							event.preventDefault()
							submit()
						}}
					>
						<h2 className="text-card-title text-foreground text-primary">
							Your question
						</h2>
						<p className="text-description text-subtle-foreground">
							Please state your query in detail for a meaningful response.
						</p>

						<Field
							className="flex flex-col gap-1.5"
							label="Title"
							labelClassName="text-primary"
							required
							hint="One line to summarize your query."
							error={errors.title}
						>
							{({ id, describedBy, invalid }) => (
								<input
									id={id}
									className={cn(controlClasses, "h-11")}
									name="title"
									value={draft.title}
									aria-describedby={describedBy}
									aria-invalid={invalid}
									onChange={event => {
										draft.setTitle(event.target.value)
									}}
								/>
							)}
						</Field>

						<Field
							className="flex flex-col gap-1.5"
							label="Description"
							labelClassName="text-primary"
							required
							hint="Explain your query in detail."
							count={{
								current: richTextVisibleLength(draft.description),
								max: QUERY_DESCRIPTION_MAX_LENGTH,
							}}
							error={errors.description}
						>
							{({ describedBy, invalid }) => (
								<TiptapEditor
									label=""
									value={draft.description}
									maxLength={QUERY_DESCRIPTION_MAX_LENGTH}
									aria-describedby={describedBy}
									aria-invalid={invalid}
									onChange={draft.setDescription}
								/>
							)}
						</Field>

						<AttachmentPicker
							files={draft.attachments}
							rejections={draft.rejections}
							onSelect={draft.addFiles}
							onRemove={draft.removeAttachment}
						/>

						{create.isError ? (
							<div
								className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-3"
								role="alert"
							>
								<span
									className="size-5 shrink-0"
									aria-hidden="true"
								>
									<TriangleAlertIcon
										className="size-5 shrink-0"
										aria-hidden="true"
									/>
								</span>
								<span>
									<span className="text-body text-foreground">
										{toDisplayable(create.error).title}
									</span>
									{toDisplayable(create.error).message}
									<span className="mt-1 flex flex-wrap gap-2">
										<button
											type="button"
											className={cn(buttonVariants({ variant: "primary" }))}
											onClick={submit}
										>
											Try sending again
										</button>
										<Link
											className={cn(buttonVariants({ variant: "ghost" }))}
											to="/queries"
										>
											Check My queries first
										</Link>
									</span>
								</span>
							</div>
						) : null}

						{create.isPending ? (
							<div
								className="rounded-card border-status-open-border bg-status-open-bg flex items-start gap-3 border p-3"
								role="status"
							>
								<span
									className="size-5 shrink-0"
									aria-hidden="true"
								>
									<ClockIcon
										className="size-5 shrink-0"
										aria-hidden="true"
									/>
								</span>
								<span>
									<span className="text-body text-foreground">
										Sending your query
										{draft.attachments.length > 0
											? ` and uploading ${String(draft.attachments.length)} file${draft.attachments.length === 1 ? "" : "s"}`
											: ""}
									</span>
									Keep this screen open. Nothing is submitted twice if the
									connection stutters. We finish the one upload that started.
								</span>
							</div>
						) : null}

						{/* THE NOTE IS NOT AN ACTION, SO IT DOES NOT GET PINNED.
					    It used to be the third child of the sticky bar, stacked
					    under the submit and the cancel, which made the bar 181px
					    tall on a phone. With the 60px tab bar beneath it, 241px of
					    an 812px screen — 30% — was chrome while somebody was
					    trying to fill in a form, and the field being typed into
					    kept sliding under it. It is a sentence you read once
					    before deciding, so it belongs above the button in the
					    ordinary flow. */}
						<p className="text-meta text-muted">
							Every available expert is alerted on WhatsApp the moment you send this.
							You cannot edit a query after sending.
						</p>

						{/* Two actions on ONE ROW rather than stacked: 68px instead of
					    the 132px the pair cost vertically, and the primary keeps
					    the width because it is the one being aimed at. */}
						<div className="border-border bg-surface sticky bottom-0 flex flex-row items-center gap-2 border-t py-3 lg:static lg:border-0 lg:bg-transparent lg:py-0">
							<button
								type="submit"
								className={cn(buttonVariants({ variant: "primary" }))}
								disabled={create.isPending}
							>
								{create.isPending ? "Sending Query…" : "Send Query"}
							</button>
							<Link
								className={cn(buttonVariants({ variant: "secondary" }))}
								to="/queries"
								onClick={draft.reset}
							>
								Cancel
							</Link>
						</div>
					</form>
				</div>

				{/* `hidden lg:flex`: the <details> above is already `lg:hidden`, so
			    without this a phone rendered BOTH — the same heading and the same
			    three items, twice down one column.

			    No `aria-label`: it repeated the <h2> word for word, and a landmark
			    named the same as its own heading is announced twice. */}
			</div>
		</div>
	)
}

/**
 * State 5 — the receipt (US-00-002.1/.30).
 *
 * Stays on this route rather than redirecting: `#<n>` is shown exactly as it
 * reads in My queries, the thread and the WhatsApp message, and the person
 * chooses where to go next instead of being sent somewhere for them.
 */
function SubmittedQuery({
	detail,
	onAskAnother,
}: {
	detail: QueryDetail
	onAskAnother: () => void
}) {
	const submittedAt = new Date(detail.submittedAt).toLocaleString("en-IN", {
		timeZone: "Asia/Kolkata",
		day: "numeric",
		month: "long",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	})

	return (
		<div
			className="flex flex-col gap-6"
			data-screen="create-query"
		>
			<div className="rounded-card border-border bg-card shadow-card flex flex-col gap-6 border p-6 sm:p-8">
				<div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
					<div className="flex items-start gap-3">
						<span
							className="bg-status-answered-bg text-status-answered-fg rounded-circle flex size-10 shrink-0 items-center justify-center"
							aria-hidden="true"
						>
							<CheckIcon
								className="size-5 shrink-0"
								aria-hidden="true"
							/>
						</span>
						<div className="flex flex-col gap-0.5 pt-0.5">
							<h2 className="text-section-title text-heading">Query sent</h2>
							<p className="text-description text-subtle-foreground max-w-prose">
								Every available expert has been alerted on WhatsApp. You will get a
								message the moment one replies.
							</p>
						</div>
					</div>
					<div className="flex shrink-0 items-center gap-2">
						<span className="text-foreground text-meta font-semibold tabular-nums">
							#{detail.sequence}
						</span>
						<span className="text-description capitalize">{detail.status}</span>
					</div>
				</div>

				<dl className="border-border flex flex-col border-t">
					<div className="border-border flex flex-wrap items-baseline justify-between gap-2 border-b py-2.5">
						<dt className="text-meta text-muted">Title</dt>
						<dd className="text-description text-foreground text-right">
							{detail.title.slice(0, 20)}
						</dd>
					</div>
					<div className="flex flex-wrap items-baseline justify-between gap-2 py-2.5">
						<dt className="text-meta text-muted">Created time</dt>
						<dd className="text-description text-foreground text-right tabular-nums">
							{submittedAt} IST
						</dd>
					</div>
				</dl>

				<div className="flex flex-wrap gap-2">
					<Link
						className={cn(buttonVariants({ variant: "primary" }))}
						to="/queries/$queryId"
						params={{ queryId: String(detail.sequence) }}
					>
						Open query #{detail.sequence}
					</Link>
					<Link
						className={cn(buttonVariants({ variant: "secondary" }))}
						to="/queries"
					>
						Go to My queries
					</Link>
					<button
						type="button"
						className={cn(buttonVariants({ variant: "ghost" }))}
						onClick={onAskAnother}
					>
						Ask another question
					</button>
				</div>
			</div>

			<div
				className="rounded-card border-border bg-card shadow-card z-toast fixed right-4 bottom-20 flex items-center gap-2 border p-3 lg:bottom-4"
				role="status"
			>
				Remembered something you left out? A sent query cannot be edited. Add it as a
				follow-up message on the thread instead.
			</div>
		</div>
	)
}

/**
 * The attachment picker — screens/create-query.html's own `.dropzone` (empty)
 * and `.files`/`.file` (chosen), not the generic FilePicker: this screen and
 * create-vetting-request.html name every one of those parts differently.
 */
function AttachmentPicker({
	files,
	rejections,
	onSelect,
	onRemove,
}: {
	files: readonly PlatformFile[]
	rejections: readonly FileRejection[]
	onSelect: (files: readonly PlatformFile[]) => void
	onRemove: (index: number) => void
}) {
	const inputRef = useRef<HTMLInputElement>(null)

	return (
		<div className="flex flex-col gap-1.5">
			<p
				className="text-field-label text-primary flex flex-wrap items-baseline gap-x-2"
				id="attachments-label"
			>
				{/* A row with a gap: these two were inline siblings with nothing
				    between them, so the label read "AttachmentsUp to 3 files". */}
				<span>Attachments</span>
				<span className="text-muted font-normal">Up to 3 files</span>
			</p>
			<p className="text-meta text-muted">PDF, JPG or PNG, 5 MB each.</p>

			<input
				ref={inputRef}
				type="file"
				className="sr-only"
				multiple
				accept={QUERY_ATTACHMENT_RULES.accepted.join(",")}
				aria-label="Add files"
				onChange={event => {
					const chosen = Array.from(event.target.files ?? []).map(toPlatformFile)
					if (chosen.length > 0) onSelect(chosen)
					event.target.value = ""
				}}
			/>

			<button
				type="button"
				className="rounded-card border-border hover:border-border-strong flex flex-col items-center gap-2 border border-dashed p-6 text-center transition-colors"
				onClick={() => inputRef.current?.click()}
			>
				<UploadIcon
					className="text-muted size-6 shrink-0"
					aria-hidden="true"
				/>
				<p className="text-description text-foreground font-medium">
					Drag files here, or choose files
				</p>
				{/* The `accept` attribute's MIME list is for the file dialog, not for
				    the reader — this printed "application/pdf, image/jpeg, image/png"
				    on screen, two lines under a line that already said "PDF, JPG or
				    PNG, 5 MB each" in words. The size is the part that was not
				    already said, so the size is what stays. */}
				<p className="text-meta text-muted">
					Up to {formatBytes(QUERY_ATTACHMENT_RULES.maxBytes)} each
				</p>
			</button>

			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary" }))}
					onClick={() => inputRef.current?.click()}
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1-2h7l1 2h2a1.5 1.5 0 0 1 1.5 1.5V18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18Z" />
						<circle
							cx="12"
							cy="13"
							r="3.25"
						/>
					</svg>
					Camera
				</button>
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary" }))}
					onClick={() => inputRef.current?.click()}
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<rect
							x="3.5"
							y="4.5"
							width="17"
							height="15"
							rx="2"
						/>
						<path d="m4 16 4.5-4.5 3 3L17 9l3 3" />
					</svg>
					Gallery
				</button>
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary" }))}
					onClick={() => inputRef.current?.click()}
				>
					<FileTextIcon
						className="size-5 shrink-0"
						aria-hidden="true"
					/>
					Files
				</button>
			</div>

			{files.length > 0 ? (
				<ul className="flex flex-col gap-2">
					{files.map((file, index) => (
						<li
							key={`${file.name}-${String(index)}`}
							className="rounded-control border-border bg-subtle flex items-center gap-2 border px-3 py-2"
						>
							<span
								className="rounded-control bg-subtle text-muted inline-flex size-10 shrink-0 items-center justify-center"
								aria-hidden="true"
							>
								<FileTextIcon
									className="size-5 shrink-0"
									aria-hidden="true"
								/>
							</span>
							<span className="flex min-w-0 flex-1 flex-col">
								<span className="text-description text-foreground truncate">
									{file.name}
								</span>
								<span className="text-meta text-muted">
									{formatBytes(file.sizeBytes)}
								</span>
							</span>
							<button
								type="button"
								className={cn(
									buttonVariants({ variant: "ghost" }),
									"text-destructive",
								)}
								onClick={() => {
									onRemove(index)
								}}
							>
								Remove
							</button>
						</li>
					))}
				</ul>
			) : null}

			<p className="text-meta text-muted">
				{QUERY_ATTACHMENT_RULES.maxFiles - files.length} slots left
			</p>

			{files.length === QUERY_ATTACHMENT_RULES.maxFiles - 1 ? (
				<div className="rounded-card border-status-in-progress-border bg-status-in-progress-bg flex items-start gap-3 border p-3">
					<span
						className="size-5 shrink-0"
						aria-hidden="true"
					>
						<TriangleAlertIcon
							className="size-5 shrink-0"
							aria-hidden="true"
						/>
					</span>
					<span>One slot left. Remove a file below to swap it for another.</span>
				</div>
			) : null}

			{rejections.map(rejection => (
				<div
					key={rejection.message}
					role="alert"
					className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-3"
				>
					<span
						className="size-5 shrink-0"
						aria-hidden="true"
					>
						<TriangleAlertIcon
							className="size-5 shrink-0"
							aria-hidden="true"
						/>
					</span>
					<span className="text-body text-foreground">{rejection.message}</span>
				</div>
			))}
		</div>
	)
}
